import type { PackDetail } from '@planuze/admin-contracts';
import { Card, CardContent, CardHeader, CardTitle } from '@planuze/ui';
import type { ReactNode } from 'react';
import { usePacksCatalogContent } from '../../content/sections/packs-catalog.js';
import { CopyableId } from '../copyable-id.js';

/* Cartão de metadata rica do pack (Fase 2, ADR-0703+): description/capabilities/tags/category/
   minAppVersion/publisherId — campos que o item da lista (publishedPackDto) não traz e que só
   chegam pelo endpoint de detalhe. Puro: recebe o `PackDetail` já resolvido. */
export function PackDetailCard({ detail }: { readonly detail: PackDetail }) {
  const copy = usePacksCatalogContent().detailCard;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{copy.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Field label={copy.description}>
          {detail.description.length > 0 ? (
            <p className="whitespace-pre-line text-sm text-foreground">{detail.description}</p>
          ) : (
            <span className="text-sm text-muted-foreground">{copy.noDescription}</span>
          )}
        </Field>
        <dl className="grid gap-4 sm:grid-cols-2">
          <Field label={copy.category}>
            {detail.category ?? <span className="text-muted-foreground">—</span>}
          </Field>
          <Field label={copy.minAppVersion}>
            <span className="font-mono text-sm text-muted-foreground">v{detail.minAppVersion}</span>
          </Field>
          {detail.publisherId !== undefined && (
            <div className="sm:col-span-2">
              <Field label={copy.publisher}>
                <CopyableId value={detail.publisherId} label={copy.publisherIdLabel} />
              </Field>
            </div>
          )}
        </dl>
        <Field label={copy.capabilities}>
          <ChipList items={detail.capabilities} empty={copy.noCapabilities} mono />
        </Field>
        <Field label={copy.tags}>
          <ChipList items={detail.tags ?? []} empty={copy.noTags} />
        </Field>
      </CardContent>
    </Card>
  );
}

function ChipList({
  items,
  empty,
  mono = false,
}: {
  readonly items: readonly string[];
  readonly empty: string;
  readonly mono?: boolean;
}) {
  if (items.length === 0) return <span className="text-sm text-muted-foreground">{empty}</span>;
  return (
    <ul className="flex flex-wrap gap-2">
      {items.map((item) => (
        <li key={item}>
          <span
            className={`rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground${
              mono ? ' font-mono' : ''
            }`}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Field({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}
